import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Share2, Smartphone, X } from 'lucide-react';
import { Button, IconButton, Text } from './ui';
import { trackEvent } from '../utils/analytics';

const DISMISS_KEY = 'postreams-pwa-install-dismissed-at';
const INSTALLED_KEY = 'postreams-pwa-installed';
const DISMISS_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

const isStandaloneMode = () => {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const isIosDevice = () => {
  const userAgent = window.navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
};

const isIosSafari = () => {
  const userAgent = window.navigator.userAgent || '';
  const vendor = window.navigator.vendor || '';

  return isIosDevice()
    && /apple/i.test(vendor)
    && /safari/i.test(userAgent)
    && !/(crios|fxios|edgios|opios|chrome|android)/i.test(userAgent);
};

const shouldRespectDismissal = () => {
  const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || '0');
  return dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS;
};

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [promptMode, setPromptMode] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth < 1024);
  const lastTrackedPromptRef = useRef(null);

  const refreshPromptVisibility = useCallback(
    (installEvent = deferredPrompt) => {
      if (window.localStorage.getItem(INSTALLED_KEY) === '1' || isStandaloneMode()) {
        window.localStorage.setItem(INSTALLED_KEY, '1');
        setPromptMode(null);
        setIsVisible(false);
        return;
      }

      if (!isMobileViewport || shouldRespectDismissal()) {
        setIsVisible(false);
        return;
      }

      if (installEvent) {
        setPromptMode('native');
        setIsVisible(true);
        return;
      }

      if (isIosSafari()) {
        setPromptMode('ios');
        setIsVisible(true);
        return;
      }

      setPromptMode(null);
      setIsVisible(false);
    },
    [deferredPrompt, isMobileViewport]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < 1024);
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      refreshPromptVisibility(event);
    };

    const handleAppInstalled = () => {
      window.localStorage.setItem(INSTALLED_KEY, '1');
      setDeferredPrompt(null);
      setPromptMode(null);
      setIsVisible(false);
      setIsInstalling(false);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    refreshPromptVisibility();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [refreshPromptVisibility]);

  useEffect(() => {
    refreshPromptVisibility();
  }, [isMobileViewport, deferredPrompt, refreshPromptVisibility]);

  useEffect(() => {
    const promptKey = isVisible && promptMode ? `${promptMode}:${isMobileViewport ? 'mobile' : 'desktop'}` : null;

    if (!promptKey || lastTrackedPromptRef.current === promptKey) {
      return;
    }

    lastTrackedPromptRef.current = promptKey;
    trackEvent('pwa_install_prompt_shown', {
      prompt_mode: promptMode,
      viewport: isMobileViewport ? 'mobile' : 'desktop',
    });
  }, [isVisible, promptMode, isMobileViewport]);

  const dismissPrompt = useCallback((reason = 'dismissed') => {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    trackEvent('pwa_install_prompt_dismissed', {
      prompt_mode: promptMode,
      reason,
    });
    setIsVisible(false);
  }, [promptMode]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    try {
      setIsInstalling(true);
      trackEvent('pwa_install_prompt_started', {
        prompt_mode: promptMode,
      });
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        window.localStorage.setItem(INSTALLED_KEY, '1');
        trackEvent('pwa_install_prompt_accepted', {
          prompt_mode: promptMode,
        });
        setIsVisible(false);
      } else {
        dismissPrompt('native_declined');
      }
    } finally {
      setDeferredPrompt(null);
      setIsInstalling(false);
    }
  }, [deferredPrompt, dismissPrompt, promptMode]);

  useEffect(() => {
    const handleInstalled = () => {
      trackEvent('pwa_installed', {
        prompt_mode: promptMode || 'unknown',
      });
    };

    window.addEventListener('appinstalled', handleInstalled);
    return () => window.removeEventListener('appinstalled', handleInstalled);
  }, [promptMode]);

  const promptCopy = useMemo(() => {
    if (promptMode === 'ios') {
      return {
        title: 'Add Postreams to your Home Screen',
        description: 'Open Postreams like a native app from Safari with faster access and a full-screen layout.',
      };
    }

    return {
      title: 'Install Postreams',
      description: 'Launch your workspace from the Home Screen and keep the app feeling fast, focused, and native.',
    };
  }, [promptMode]);

  if (!isVisible || !promptMode) return null;

  return (
    <div className='install-prompt fixed inset-x-0 z-30 px-4 lg:hidden'>
      <div className='install-prompt-card mx-auto max-w-md rounded-[1.5rem] p-4'>
        <div className='flex items-start gap-3'>
          <div
            className='mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl'
            style={{
              background: 'rgba(45, 212, 191, 0.14)',
              color: 'var(--brand-dark)',
            }}
          >
            {promptMode === 'ios' ? <Smartphone className='h-5 w-5' /> : <Download className='h-5 w-5' />}
          </div>

          <div className='min-w-0 flex-1'>
            <Text variant='bodySmall' className='mb-1 font-semibold' style={{ color: 'var(--text-primary)' }}>
              {promptCopy.title}
            </Text>
            <Text variant='caption' className='mb-0' style={{ color: 'var(--text-body)' }}>
              {promptCopy.description}
            </Text>
          </div>

          <IconButton icon={X} onClick={() => dismissPrompt('close_button')} variant='ghost' size='sm' aria-label='Dismiss install prompt' />
        </div>

        {promptMode === 'ios' ? (
          <div
            className='mt-4 rounded-2xl p-3'
            style={{
              background: 'rgba(148, 163, 184, 0.08)',
              border: '1px solid var(--border)',
            }}
          >
            <div className='flex items-center gap-2'>
              <Share2 className='h-4 w-4' style={{ color: 'var(--brand)' }} />
              <Text variant='caption' className='mb-0 font-semibold' style={{ color: 'var(--text-primary)' }}>
                Safari install steps
              </Text>
            </div>
            <Text variant='caption' className='mt-2 mb-0' style={{ color: 'var(--text-body)' }}>
              Tap <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>Share</span>, then choose <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>Add to Home Screen</span>.
            </Text>
          </div>
        ) : null}

        <div className='mt-4 flex items-center justify-end gap-2'>
            <Button type='button' variant='secondary' size='sm' onClick={() => dismissPrompt('not_now')}>
              Not now
            </Button>

          {promptMode === 'native' ? (
            <Button type='button' variant='primary' size='sm' onClick={handleInstall} loading={isInstalling}>
              Install app
            </Button>
          ) : (
            <Button type='button' variant='primary' size='sm' onClick={() => dismissPrompt('ios_acknowledged')}>
              Got it
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
